<template>
  <div class="anker">
    <Breadcrumb
      :items="breadcrumb"
      class="anker__breadcrumb"
    />
    <Heading class="sr-only">Anker</Heading>
    <div class="anker__video">
      <VideoPlayer
        :key="videoSource"
        class="anker__video-player"
        :src="videoSource"
        :autoplay="true"
        loop
        muted
        :controls="false"
        video-id="anker-video"
        playsinline
        :play-button-centered="false"
      />
      <div class="anker__video-overlay-wrapper">
        <Arrow
          direction="left"
          set-position
          class="anker__video-overlay-button anker__video-overlay-button--right"
          aria-label="Nach links scrollen"
          @change="scrollLeft"
        />
        <div
          ref="scrollableElement"
          class="anker__video-overlay"
        >
          <div
            v-for="product in HEADER_DATA.overlay.products"
            :key="product.title"
            class="anker__video-overlay-item"
            @click="navigateTo(product.anchor)"
          >
            <CdnImage
              class="anker__video-overlay-image"
              :style="product.imgStyle"
              :src="product.img.sm"
              :src-md="product.img.md"
              :alt="product.title"
              :lazy="product.lazy"
            />
            <span>{{ product.title }}</span>
          </div>
        </div>
        <Arrow
          direction="right"
          set-position
          class="anker__video-overlay-button anker__video-overlay-button-right"
          aria-label="Nach rechts scrollen"
          @change="scrollRight"
        />
      </div>
    </div>
    <Heading
      class="anker__navigation-heading"
      align="center"
      tag="h2"
      font-weight="ultra"
    >
      Kompakte Ladegeräte für ultraschnelles Laden
    </Heading>
    <div class="anker__animated-teaser-wrapper">
      <ProductOfferTileXlContainer
        v-for="(teaser, index) in highlightOffers"
        :key="teaser.type"
        :offer="teaser.offer"
        :video="teaser.video"
        :title="teaser.title"
        :ambient-image="teaser.placeholder"
        :video-button-position="index % 2 === 0 ? 'right' : 'left'"
        :video-aria-label="`Informationsvideo für ${teaser.offer?.product?.name}`"
        class="anker__animated-teaser"
        :class="teaser.type"
        :alignment="index % 2 === 0 ? 'left' : 'right'"
        lazy
      >
        <template #heading>
          <Heading
            class="product-offer-tile-xl__title"
            tag="h3"
            font-weight="ultra"
            size="xl"
          >
            {{ teaser.title }}
          </Heading>
          <div
            class="product-offer-tile-xl__description"
            v-html="teaser.copy"
          />
        </template>
      </ProductOfferTileXlContainer>
    </div>
    <div class="anker__kabel-teaser">
      <KabelTeaser />
    </div>

    <ProductOfferTileSMContainer
      :items="getOffersByGroup(offers, 'first', 3)"
      :no-delay-animation="true"
      tile-type="small"
      heading-tag="h3"
    />
    <Heading
      class="anker__outdoor-heading"
      align="center"
      tag="h2"
      font-weight="ultra"
    >
      Kabellose Ladegeräte für noch mehr Mobilität
    </Heading>
    <OutdoorStage
      id="kabellose-ladegeraete"
      :offers="getOffersByGroup(offers, 'outdoor-stage', 2)"
    />
    <GridContainer class="anker__fact-teaser">
      <CdnImage
        class="anker__fact-teaser-background"
        alt="ANKER Zahlen und Fakten"
        src="/content/content-images/landingpages/anker/fact-teaser/e2815cdc5fc3d506310725365072843e/zahlen-fakten-bg-xs.jpg"
        src-sm="/content/content-images/landingpages/anker/fact-teaser/ba6e3a315bb7953506924c998ca61bf7/zahlen-fakten-bg-sm.jpg"
        src-md="/content/content-images/landingpages/anker/fact-teaser/484c7b5155f1a014066ad433ec7d1c57/zahlen-fakten-bg-md.jpg"
        lazy
      />
      <GridRow>
        <GridColumn>
          <Heading
            class="anker__fact-teaser-title"
            align="center"
            tag="h2"
            spaces-bottom
            size="md"
            font-weight="ultra"
            >Anker – die Nr. 1 für mobiles Laden
          </Heading>
        </GridColumn>
      </GridRow>
      <GridRow>
        <GridColumn>
          <Paragraph
            align="center"
            class="anker__fact-teaser-text"
            >Über</Paragraph
          >
          <Paragraph
            align="center"
            class="anker__fact-teaser-text--highlight"
            ><Counter
              :value="200"
              :duration="500"
          /></Paragraph>
          <Paragraph
            align="center"
            class="anker__fact-teaser-text"
            >Millionen</Paragraph
          >
          <Paragraph align="center">Über 200 Millionen verkaufte Artikel weltweit</Paragraph>
        </GridColumn>
        <GridColumn>
          <div class="anker__fact-teaser-spacing"></div>
          <Paragraph
            align="center"
            class="anker__fact-teaser-text--highlight"
            ><Counter
              :value="146"
              :duration="500"
          /></Paragraph>
          <Paragraph
            align="center"
            class="anker__fact-teaser-text"
            >Länder</Paragraph
          >
          <Paragraph align="center">Präsent in 146 Ländern</Paragraph>
        </GridColumn>
        <GridColumn>
          <div class="anker__fact-teaser-spacing"></div>
          <Paragraph
            class="anker__fact-teaser-text--highlight"
            align="center"
            ><Counter
              :value="10"
              :duration="500"
          /></Paragraph>
          <Paragraph
            align="center"
            class="anker__fact-teaser-text"
            >Jahre Innovation</Paragraph
          >
          <Paragraph align="center"
            >Seit 10 Jahren Spitzenreiter in smarter Ladetechnologie</Paragraph
          >
        </GridColumn>
      </GridRow>
    </GridContainer>
    <GridContainer>
      <ImageTeaser
        tile-size="m"
        description="USB-Kabel von Anker sind besonders flexibel, hochwertig und robust, sowie mit
            innovativen Technologien ausgestattet."
        animate-tile
        heading-tag="h2"
        :animation-delay="1"
        class="anker__banner anker__banner--tall-sm anker__banner--kabel"
      >
        <template #bgImage>
          <CdnImage
            class="anker__banner-image"
            src="/content/content-images/landingpages/anker/banner-teasers/2e51422def7d5065c76e48477ef4e4ad/teaser-kabel-s.jpg"
            src-sm="/content/content-images/landingpages/anker/banner-teasers/b3629698d65ccb144a47aff841fdb2f5/teaser-kabel-m.jpg"
            src-md="/content/content-images/landingpages/anker/banner-teasers/c4bf552715e9ab78172657988c47ff7e/teaser-kabel-l.jpg"
            src-lg="/content/content-images/landingpages/anker/banner-teasers/6f361b9cf3c89c1f46b2fdd19bbe5456/teaser-kabel-l@2x.jpg"
            alt="Anker USB Kabel"
            lazy
            sizes="xs:100vw sm:100vw lg:100vw"
          />
        </template>
        <template #heading>
          <div>
            <Heading
              class="anker__banner-title"
              font-weight="bold"
              tag="h2"
            >
              Hochwertig, Flexibel, Vielseitig
            </Heading>
            <Heading
              class="anker__banner-heading"
              tag="h3"
              size="md"
              font-weight="ultra"
              spaces-bottom
            >
              Anker USB Kabel
            </Heading>
          </div>
        </template>
        <template #button>
          <LinkButton
            to="/zubehoer?search=anker-cable"
            primary
            tag="nuxt-link"
          >
            Alle Kabel entdecken
          </LinkButton>
        </template>
      </ImageTeaser>

      <ImageTeaser
        tile-size="m"
        animate-tile
        lazy
        heading-tag="h2"
        :animation-delay="2"
        class="anker__banner anker__banner--tall-sm anker__banner--ladepads"
      >
        <template #bgImage>
          <CdnImage
            class="anker__banner-image"
            src="/content/content-images/landingpages/anker/banner-teasers/54a4ed1b4dc6c57cbe07899bba48d29b/zubehoer-produkte.jpg"
            src-md="/content/content-images/landingpages/anker/banner-teasers/aeb3c9fc815fda8983377ccaaad9be8b/zubehoer-produkte@2x.jpg"
            alt="Anker Ladepads"
            lazy
            sizes="xs:200vw sm:100vw lg:100vw"
          />
        </template>
        <template #heading>
          <div>
            <Heading
              class="anker__banner-heading anker__banner-heading--small-mobile"
              tag="h2"
              size="md"
              font-weight="ultra"
              spaces-bottom
            >
              Entdecke weitere <br />
              Anker Produkte
            </Heading>
          </div>
        </template>
        <template #button>
          <LinkButton
            to="/zubehoer/_anker"
            primary
            tag="nuxt-link"
          >
            Alle Anker Produkte
          </LinkButton>
        </template>
      </ImageTeaser>
    </GridContainer>
  </div>
</template>

<script lang="ts">
import { ref } from 'vue';
import PageTracking from '@/mixins/page-tracking';
import { getOffersByGroup, loadOffersForPromotions } from '@/lib/offers-by-group';
import { mapGetters } from 'vuex';

import KabelTeaser from '@/components/anker/kabel-teaser.vue';
import {
  Arrow,
  LinkButton,
  Heading,
  Paragraph,
  GridContainer,
  ImageTeaser,
  GridRow,
  GridColumn,
  VideoPlayer,
} from '@i22-td-smarthome/component-library';
// @ts-ignore
import Breadcrumb from '@/components/breadcrumb.vue'; // @ts-ignore
import CdnImage from '@/components/cdn-image.vue'; // @ts-ignore
import ProductOfferTileSMContainer from '@/components/product-offer-tile-s-m-container.vue'; // @ts-ignore
import { HEADER_DATA, PRODUCT_TEASER_DATA, NAVIGATION_DATA } from './product-data';
import OutdoorStage from '@/components/anker/outdoor-stage.vue';
import { getHeader } from '@/helpers/page-header-compositon';
import Counter from '@/components/anker/counter.vue';
import ProductOfferTileXlContainer from '@/components/product-offer-tile-xl-container.vue';
import { Track } from '~/helpers/page-tracking';

export default {
  name: 'Anker',
  setup() {
    Track({ page_type: 'theme.landing' });

    getHeader({
      title: ref('Anker Produkte entdecken'),
      description: ref(
        'Ob kompakte Ladegeräte, Powerbanks oder mobile Stromquellen für Zuhause und Unterwegs: Jetzt die innovativen Produkte von Anker entdecken & bestellen!'
      ),
    });
  },
  scrollToTop: true,
  components: {
    Arrow,
    CdnImage,
    Breadcrumb,
    ProductOfferTileSMContainer,
    VideoPlayer,
    KabelTeaser,
    GridContainer,
    GridRow,
    GridColumn,
    Paragraph,
    Heading,
    LinkButton,
    OutdoorStage,
    Counter,
    ProductOfferTileXlContainer,
    ImageTeaser,
  },
  mixins: [PageTracking()],
  head() {
    return this.pageMeta;
  },
  async asyncData() {
    return { offers: await loadOffersForPromotions([...PRODUCT_TEASER_DATA, ...NAVIGATION_DATA]) };
  },
  data() {
    return {
      breadcrumb: [
        {
          label: 'Anker',
          to: 'anker',
        },
      ],
      HEADER_DATA,
      NAVIGATION_DATA,
      offers: [],
      video: {
        mobile:
          '/content/content-videos/landingpages/anker/f875c4ba15433f477c54da2cd1f335bc/header-mobile.webm',
        desktop:
          '/content/content-videos/landingpages/anker/875bf3aa8a05de60e03ea8d59ba24bf2/header-desktop.webm',
      },
      selectedHighlightId: 'nano',
    };
  },
  computed: {
    ...mapGetters('coupon', ['couponInfo', 'isValidCoupon']),
    tealiumPageType() {
      return 'theme.landing';
    },
    tealiumType() {
      return 'theme.landing';
    },
    videoSource() {
      return this.$screenSize.isMobileScreen.value ? this.video.mobile : this.video.desktop;
    },
    highlightOffers() {
      return getOffersByGroup(this.offers, 'highlight');
    },
  },
  layout: 'error',
  methods: {
    getOffersByGroup,
    scrollToTarget(target: string) {
      const el = document.querySelector(target);
      if (el) {
        const elementPosition = el.getBoundingClientRect().top;
        window.scrollTo({
          top: elementPosition + window.scrollY - 250,
          behavior: 'smooth',
        });
      }
    },
    navigateTo(target: string | { type: string } | { action: string; url: string }) {
      if (typeof target === 'object') {
        if (['nano', 'ganprime'].includes(target.type)) {
          this.selectAnimationType(target.type);
        } else if (target.action && target.url) {
          const routeData = this.$router.resolve(target.url);
          window.open(routeData.href, '_blank');
        }
      } else {
        this.scrollToTarget(target);
      }
    },
    selectAnimationType(type: string) {
      this.selectedHighlightId = type;
      this.scrollToTarget(`.${type}`);
    },
    scrollLeft() {
      const scrollableElement = this.$refs.scrollableElement as HTMLElement;
      if (scrollableElement) {
        scrollableElement.scrollBy({ left: -400, behavior: 'smooth' });
      }
    },
    scrollRight() {
      const scrollableElement = this.$refs.scrollableElement as HTMLElement;
      if (scrollableElement) {
        scrollableElement.scrollBy({ left: 400, behavior: 'smooth' });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.anker {
  &__video {
    position: relative;
    @include mq($mq-sm) {
      padding-bottom: 75px;
    }

    &-player {
      :deep(video) {
        aspect-ratio: 435 / 356;

        @include mq($mq-sm) {
          aspect-ratio: 1920 / 820;
        }
      }
    }

    &-overlay {
      display: flex;
      flex-direction: row;
      align-items: center;
      border-radius: 20px;
      background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.6) 25%,
        rgba(255, 255, 255, 0.9) 40.19%,
        rgba(255, 255, 255, 1) 55.19%,
        #ffffff 100%
      );
      overflow: scroll;
      grid-gap: 4 * $grid-base;
      padding: 3 * $grid-base $grid-base;
      @include mq($mq-sm) {
        margin: 0 2 * $grid-base;
        z-index: 1;
        margin-top: -100px;
        position: relative;
      }
      @include mq(790px) {
        justify-content: center;
        margin-top: -150px;
      }
      @include mq($mq-xl) {
        margin-top: -200px;
      }
      &::-webkit-scrollbar {
        display: none;
      }
      &-item {
        text-align: center;
        transition: all 0.3s ease-in-out;
        cursor: pointer;
        font-weight: 600;
        &:hover {
          transform: scale(1.1);
          color: var(--color-primary);
        }

        span {
          display: inline-block;
          margin-top: 0.5 * $grid-base;
          max-width: 10 * $grid-base;
        }
      }
      &-image {
        display: block;
        height: 125px;
        min-width: 150px;
        max-width: 150px;
        margin-bottom: $grid-base;
        width: auto;

        :deep(img) {
          width: 100%;
          aspect-ratio: var(--aspect-ratio);
        }
      }

      &-wrapper {
        position: relative;
      }

      &-button {
        padding: 2 * $grid-base 10px;
        background: rgba(178, 178, 178, 0.7);
        border: none;

        @include mq(790px) {
          display: none;
        }
      }
    }
  }

  &__coupon-section {
    margin: 0 2 * $grid-base 3 * $grid-base 2 * $grid-base;
    background: linear-gradient(180deg, #ffffff 0%, #ededed 100%);
  }

  &__heading {
    @include font-size(36px, 36px);
  }

  &__navigation {
    margin-bottom: 3 * $grid-base;
    &-heading {
      @include font-size(36px, 36px);
      margin-bottom: 3 * $grid-base;
      padding: $grid-base;
      @include mq($mq-md) {
        @include font-size(48px, 54px);
      }
    }
    &-img {
      width: 120px;
      margin: 0 auto;
      display: block;
      margin-bottom: $grid-base;
    }
    &--hoverable {
      cursor: pointer;
      transition: all 0.2s ease-in-out;
      &:hover {
        transform: scale(1.1);
        color: var(--color-primary);
        font-weight: 800;
      }
    }
    &--active {
      transition: all 0.2s ease-in-out;
      transform: scale(1.1);
      color: var(--color-primary);
      font-weight: 800;
    }
    &-title {
      @include font-size(18px, 24px);
      margin-bottom: $grid-base;
      &--active {
        transition: all 0.2s ease-in-out;
        color: var(--color-primary);
        font-weight: 800;
      }
    }
    p {
      max-width: 200px;
      margin-inline: auto;
    }
  }

  &__animated-teaser {
    :deep(.anker-list) {
      list-style-type: square;
      margin-left: 2 * $grid-base;
    }

    &-wrapper {
      display: flex;
      flex-direction: column;
      gap: 3 * $grid-base;

      @include mq($mq-md) {
        gap: 6 * $grid-base;
      }
    }

    :deep(.product-offer-tile-xl__column-image) {
      width: 100%;
    }

    :deep(.video-player) video {
      aspect-ratio: 16 / 9;
    }

    :deep(.product-offer-detail-link__link) {
      img {
        aspect-ratio: 1 / 1;
        width: 100%;
      }
    }
  }

  &__kabel-teaser {
    max-width: $mq-xl;
    margin: 5 * $grid-base auto;
  }

  @include mq($mq-sm) {
    margin: 0;
  }

  @include mq($mq-md) {
    max-height: none;
  }

  &__outdoor-heading {
    margin: 2 * $grid-base auto;
    padding: $grid-base;
    @include font-size(36px, 36px);
    @include mq($mq-md) {
      @include font-size(48px, 54px);
    }
  }

  &__fact-teaser {
    position: relative;
    margin: 3 * $grid-base auto;
    padding: 5 * $grid-base 0;

    &-background {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      z-index: -1;
      overflow: hidden;

      :deep(img) {
        min-width: 100%;
        min-height: 100%;
        width: 100%;
        object-fit: cover;
        aspect-ratio: 640 / 957;
        object-position: left;

        @include mq($mq-sm) {
          aspect-ratio: 1024 / 597;
          object-position: center;
        }

        @include mq($mq-md) {
          aspect-ratio: 640 / 191;
        }
      }
    }
    &-text {
      @include font-size(24px, 29px);
      &--highlight {
        color: color(magenta, 1000);
        @include font-size(60px, 60px);
        font-weight: 800;
      }
    }
    &-spacing {
      // height of one paragraph + padding
      height: 48px;
    }
  }

  &__banner {
    min-height: 340px;
    margin-bottom: 3 * $grid-base;
    @include mq($mq-sm) {
      min-height: 420px;
    }
    &--tall-sm {
      align-items: flex-start;
      @include mq(false, $mq-sm) {
        min-height: 580px;
      }
    }
    &-title {
      font-weight: 700;
      margin-bottom: calc($grid-base / 2);
      @include font-size(18px, 24px);
    }

    :deep(.image-teaser__container) {
      width: 100%;
    }

    :deep(.image-teaser__main) {
      @include mq($mq-sm) {
        width: 50%;
      }
    }

    &--kabel {
      .anker__banner-image {
        :deep(img) {
          aspect-ratio: 32 / 29;
          @include mq($mq-sm) {
            aspect-ratio: 16 / 5;
          }
          @include mq($mq-md) {
            aspect-ratio: 640 / 147;
          }
          @include mq($mq-lg) {
            aspect-ratio: 61 / 14;
          }
        }
      }
    }

    &--ladepads {
      .anker__banner-image {
        :deep(img) {
          aspect-ratio: 1024 / 309;
          @include mq($mq-md) {
            aspect-ratio: 732 / 221;
          }
        }
      }
    }
  }
}

:deep(.coupon-teaser--mini .coupon-teaser__hero-image) {
  max-width: 240px;
}

:deep(.banner-teaser) {
  padding: $grid-base;
  @include mq($mq-sm) {
    padding: 2 * $grid-base;
  }
}

:deep(.pot-sm-container .grid-container) {
  padding: 2 * $grid-base;
  overflow: hidden;
  @include mq($mq-xl) {
    padding: 0;
  }
}

:deep(.product-offer-tile-xl__product-image) {
  width: 50%;
}

:deep(.product-offer-tile-xl__description) {
  margin-bottom: 2 * $grid-base;
}
</style>
